import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import AutoComplete, { AutoCompleteOption } from "../common/AutoComplete";
import { nameof } from "../common/ObjectUtils";
import Manipulate from "./AutocompleteManipulateValues";
import { TransferContext } from "./TransferContext";
import TransferReasonService from "./TransferReasonService";
import { TransferReasonTypes } from "./Types";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import {OperationScope, ReasonCode} from "../../config";
import {useHasScope} from "../../../../config/globalConfig";

interface Props {
  setPage: (value: boolean) => void;
  updateDatasourceAcriss: () => void;
  handleNext: () => void;
  formik: any;
  tenant: string;
  setChangeStartStation: (value: boolean) => void;
  setChangeTransportationType: (value: boolean) => void;
  setMinDateTime: (value: any) => void;
  minDateTime: any;
}

const GeneralInfo = (props: Props) => {
  const { i18n } = useTranslation();
  const { globalData } = useContext(GlobalContext);
  const manipulate = Manipulate();
  const snackbar = useSnackbar();
  const context = useContext(TransferContext);
  const { setLoading, userLocations, allLocations } = useContext(ContextOperation);
  const t = useOperationTranslation();
  const [startEndLocation, setStartEndLocation] = useState<
    AutoCompleteOption[]
  >([]);
  const [allLocationFilteredOptions, setAllLocationFilteredOptions] = useState<AutoCompleteOption[]>(context.getAllLocation);
  const [reasonFilteredOptions, setReasonFilteredOptions] = useState<AutoCompleteOption[]>(context.reason);

  const closeModal = () => {
    props.setPage(false);
  };

  const disableButton = () => {
    if (
      props.formik.values.transferReasonCode !== "" &&
      props.formik.values.transferReasonCode !== undefined &&
      props.formik.values.startStationId !== "" &&
      props.formik.values.startStationId !== undefined &&
      props.formik.values.expectedEndStationId !== "" &&
      props.formik.values.expectedEndStationId !== undefined &&
      props.formik.values.expectedEndDate !== null &&
      props.formik.values.expectedEndDate !== undefined &&
      props.formik.values.transferType !== "" &&
      props.formik.values.transferType !== undefined &&
      props.formik.values.startDate !== null &&
      props.formik.values.startDate !== undefined &&
      dayjs(props.formik.values?.startDate).isBefore(
        dayjs(props.formik.values?.expectedEndDate)
      ) &&
      dayjs(props.formik.values?.startDate).isAfter(dayjs(props.minDateTime))
    )
      return false;
    else return true;
  };

  /*
    is SCOPE_VIEW_MDTRANSPORTATION_CARTRANSPORTER_LABEL ? ALL options :  only driver
  */
    const transportationType: AutoCompleteOption[] =
        useHasScope(OperationScope.ViewMdTransportationCartransporterScope)
            ? [
                { value: "CAR_TRANSPORTER", label: t("transferReason.autocomplete.carTransporter") },
                { value: "DRIVER", label: t("transferReason.autocomplete.driver") },
              ]
            : [
                { value: "DRIVER", label: t("transferReason.autocomplete.driver") }
              ];

  const submitGeneralInfo = () => {
    const data: TransferReasonTypes.AddFunctionTypes = props.formik.values;
    
    const startStation = userLocations.find(loc => loc.key === data.startStationId);
    data.startStationId = startStation?.bpCode || "";
    data.startStationType = startStation?.typeEnum;

    const endStation = userLocations.find(loc => loc.key === data.endStationId);
    data.endStationId = endStation?.bpCode || "";
    data.endStationType = endStation?.typeEnum;

    const expectedEndStation = userLocations.find(loc => loc.key === data.expectedEndStationId);
    data.expectedEndStationId = expectedEndStation?.bpCode || "";
    data.expectedEndStationType = expectedEndStation?.typeEnum;

    if (props.formik.values.id == "") {
      setLoading(true);
      TransferReasonService.put
        .createTransferReason(props.tenant, data)
        .then(() => {
          props.setPage(false);
          props.updateDatasourceAcriss();
          setLoading(false);
          snackbar.show("success", t("transferReason.messages.successEdit"));
        })
        .catch(() => {
          setLoading(false);
          snackbar.show("error", t("transferReason.messages.errorEdit"));
        });
    } else {
      setLoading(true);
      TransferReasonService.put
        .editTransferReason(props.formik.values.id, data)
        .then(() => {
          props.setPage(false);
          props.updateDatasourceAcriss();
          setLoading(false);
          snackbar.show("success", t("transferReason.messages.successEdit"));
        })
        .catch(() => {
          setLoading(false);
          snackbar.show("error", t("transferReason.messages.errorEdit"));
        });
    }
  };

  useEffect(() => {
    manipulate
      .getLocationsByFilters({
        tenant: props.tenant,
        type: ["LMS", "COMPOUND", "PARKING_GO"],
        pageNumber: 0,
        pageSize: 1000000,
      })
      .then((response) => {
        setStartEndLocation(response);
      });
  }, [props.tenant, i18n.language]);

  const locations = useMemo<AutoCompleteOption[]>(() => {
    return (
      userLocations.map((loc) => ({
        label: loc.descr || "",
        value: loc.key || "",
      })) || []
    );
  }, [userLocations]);

  useEffect(() => {

    props.formik.setFieldValue(nameof<TransferReasonTypes.AddFunctionTypes>("expectedEndStationId"), "");
    props.formik.setFieldValue(nameof<TransferReasonTypes.AddFunctionTypes>("expectedEndStationType"), "");

    if(props.formik.values.transferReasonCode == ReasonCode.TFWORK) {
      setAllLocationFilteredOptions(context.getAllLocation.filter(l => l.value.toString().includes("SHOP_REPAIR")));
    }
    else if(!!props.formik.values.transferReasonCode && props.formik.values.transferReasonCode !== ReasonCode.TFWORK){
      setAllLocationFilteredOptions(context.getAllLocation.filter(l => !l.value.toString().includes("SHOP_REPAIR")));
    }
    else {
      setAllLocationFilteredOptions(context.getAllLocation);
    }

  }, [props.formik.values.transferReasonCode])

  useEffect(() => {

    setReasonFilteredOptions(context.reason.filter(r => ![ReasonCode.BTF.valueOf(), ReasonCode.DDT.valueOf()].includes(r.value.toString())));

  }, [context.reason])

  return (
    <Box>
      <Box
        sx={{
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "16px",
          color: "#18305B",
        }}
      >
        {t("transferReason.add.generalInfo")}
      </Box>
      <Box pb={6}>
        <Grid
          container
          alignItems="flex-start"
          mr={0}
          direction="row"
          columns={12}
          columnSpacing={4}
          rowGap={5.5}
        >
          <Grid item xs={3}>
            <AutoComplete
              fullWidth
              label={t("transferReason.add.transferReasonCode")}
              value={props.formik.values.transferReasonCode}
              options={reasonFilteredOptions}
              onChange={(value) =>
                props.formik.setFieldValue(
                  nameof<TransferReasonTypes.AddFunctionTypes>(
                    "transferReasonCode"
                  ),
                  value
                )
              }
            />
          </Grid>
          <Grid item xs={3}>
            <AutoComplete
              fullWidth
              label={t("transferReason.add.startStationId")}
              value={`${props.formik.values.startStationId}-${props.formik.values.startStationType}`}
              options={locations}
              onChange={(value) => {
                const location = userLocations.find(loc => loc.key === value);
                props.formik.setFieldValue(nameof<TransferReasonTypes.AddFunctionTypes>("startStationId"), location?.bpCode);
                props.formik.setFieldValue(nameof<TransferReasonTypes.AddFunctionTypes>("startStationType"), location?.typeEnum);
                props.setChangeStartStation(true);
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <>
              <DateTimePicker
                onChange={(e: any) => {
                  props.setMinDateTime(dayjs(new Date()).subtract(1, "m"));
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.AddFunctionTypes>("startDate"),
                    dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                  );
                }}
                minDateTime={props.minDateTime}
                value={props.formik.values.startDate}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.add.startDate")}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="startDate"
                    />
                  </>
                )}
              />
            </>
          </Grid>
          <Grid item xs={3}>
            <AutoComplete
              fullWidth
              label={t("transferReason.add.expectedEndStationId")}
              options={allLocationFilteredOptions}
              value={`${props.formik.values.expectedEndStationId}-${props.formik.values.expectedEndStationType}`}
              onChange={(value) => {
                const location = allLocations.find(loc => loc.key === value);
                props.formik.setFieldValue(nameof<TransferReasonTypes.AddFunctionTypes>("expectedEndStationId"), location?.bpCode);
                props.formik.setFieldValue(nameof<TransferReasonTypes.AddFunctionTypes>("expectedEndStationType"), location?.typeEnum);
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <DateTimePicker
              onChange={(e: any) => {
                props.formik.setFieldValue(
                  nameof<TransferReasonTypes.AddFunctionTypes>(
                    "expectedEndDate"
                  ),
                  dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                );
              }}
              value={props.formik.values.expectedEndDate}
              mask={"__/__/____ __:__"}
              inputFormat={"DD/MM/YYYY HH:mm"}
              label={t("transferReason.add.expectedEndDate")}
              minDateTime={dayjs(props.formik.values.startDate)?.add(1, "m")}
              ampm={false}
              renderInput={(params) => (
                <>
                  <TextField
                    variant="standard"
                    fullWidth
                    {...params}
                    name="expectedEndDate"
                  />
                </>
              )}
            />
          </Grid>
          <Grid item xs={3}>
            <AutoComplete
              fullWidth
              label={t("transferReason.add.transportationType")}
              options={transportationType}
              value={props.formik.values.transferType}
              onChange={(value) => {
                props.formik.setFieldValue(
                  nameof<TransferReasonTypes.AddFunctionTypes>("transferType"),
                  value
                );
                props.setChangeTransportationType(true);
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container direction="row" justifyContent="flex-end" spacing={2}>
        <Grid item>
          <Button
            sx={{
              maxWidth: "246px",
              maxHeight: "48px",
              minWidth: "246px",
              minHeight: "48px",
            }}
            variant="outlined"
            onClick={closeModal}
          >
            <Typography
              sx={{
                textTransform: "capitalize",
                fontWeight: 700,
                lineHeight: 2.25,
                color: "#C8017D",
              }}
              variant="subtitle1"
            >
              {t("actionButtons.cancel")}
            </Typography>
          </Button>
        </Grid>
        <Grid item>
          <Button
            sx={{
              maxWidth: "246px",
              maxHeight: "48px",
              minWidth: "246px",
              minHeight: "48px",
            }}
            type="submit"
            form="transfer-reason"
            disabled={disableButton()}
            variant="contained"
            color="accent"
            onClick={
              props.formik.values.id == ""
                ? props.handleNext
                : submitGeneralInfo
            }
          >
            <Typography
              sx={{
                textTransform: "capitalize",
                fontWeight: 700,
                lineHeight: 2.25,
                color: "white",
              }}
              variant="subtitle1"
            >
              {props.formik.values.id == ""
                ? t("transferReason.navigation.continue")
                : t("actionButtons.save")}
            </Typography>
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeneralInfo;
